/* eslint-disable no-magic-numbers */
import { ReactNode } from 'react';

import { AvailableLineDataRefNode, BasicPaymentType, NonCashPaymentType, SinglePaymentType } from '@futrli-sdk/predict';
import {
  BasicAssumption,
  FormulaAssumption,
  FullAssumptionRequest,
  FullAssumptionResponse,
  RepeatingAssumption,
  UnitAssumption
} from '@futrli-sdk/consolidations';

import { ASSUMPTION_TYPES, PAYMENT_TYPES } from '../../core/constants';
import { AllowedAssumptionTypes, AllowedPaymentTypes } from '../detail/reports/actions/actions';

export enum PredictionType {
  FORMULA = 'formula',
  FREESTYLE = 'freestyle',
  REPEATING = 'repeating',
  UNIT = 'unit'
}

export enum RepeatingPattern {
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  annually = 'annually'
}

export enum EndDateType {
  end_never = 'never',
  end_after = 'after-repeats',
  end_on_date = 'on-date'
}

export enum PaymentTreatment {
  SAME_DAY = 'same_day', //single
  SINGLE = 'single', //single
  MULTI_PAYMENT_DAYS = 'multi-payment-days', //basic
  // MULTI_PAYMENT_WEEKS = 'multi-payment-weeks'
  NON_CASH_TRANSFER = 'non-cash-transfer' // non-cash
}

export type AccountCategory =
  | 'income'
  | 'other_income'
  | 'expenses'
  | 'other_expenses'
  | 'cost_of_sales'
  | 'bank_accounts'
  | 'current_assets'
  | 'non_current_assets'
  | 'fixed_assets'
  | 'current_liabilities'
  | 'non_current_liabilities'
  | 'equity'
  | 'cash_flow';

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export type Metrics =
  | 'gross_profit'
  | 'gross_profit_%'
  | 'operating_profit'
  | 'operating_profit_%'
  | 'net_profit'
  | 'net_profit_%'
  | 'total_assets'
  | 'total_liabilities'
  | 'net_assets'
  | 'current_year_earnings'
  | 'currency_translation_difference'
  | 'transfers_to_retained_earnings'
  | 'cash_from_business_activities'
  | 'cash_from_financing_and_investing'
  | 'starting_balance'
  | 'cash_in'
  | 'cash_out'
  | 'net_cash_flow'
  | 'closing_balance';

export type NodeStaticId = AccountCategory | Metrics | AccountType;

export const TOTALS_CODE = '-1_TOTAL';

export const DELETED_REFERENCE_CODE = 'DELETED_REFERENCE';

export enum FormulaError {
  NO_ERROR = 0,
  ERROR_CIRCULAR_REFERENCE = 1,
  ERROR_INVALID_REFERENCE = 2,
  ERROR_INVALID_SYNTAX = 3,
  ERROR_VALUES_OUT_OF_EXPECTED_RANGE = 1000
}

export const paymentTreatmentTypes = {
  [PaymentTreatment.SAME_DAY]: PAYMENT_TYPES.SINGLE,
  [PaymentTreatment.SINGLE]: PAYMENT_TYPES.SINGLE,
  [PaymentTreatment.MULTI_PAYMENT_DAYS]: PAYMENT_TYPES.BASIC,
  [PaymentTreatment.NON_CASH_TRANSFER]: PAYMENT_TYPES.NON_CASH
};

export enum IncreaseDecreaseRepeating {
  never = 'no-change',
  increaseDecreaseByPercentage = 'percentage',
  increaseDecreaseByAmount = 'fixed-amount'
}

export enum QuickActionsType {
  setSelectedTo = 'set-selected-to',
  increaseByPercentage = 'increase-by-percentage',
  decreaseByPercentage = 'decrease-by-percentage',
  increaseByAmount = 'increase-by-amount',
  decreaseByAmount = 'decrease-by-amount',
  splitBetweenSelected = 'split-between-selected'
}

export interface ManualPrediction {
  id?: number;
  type: PredictionType;
  startDate: Date;
  paymentTreatment: PaymentTreatment;
  endDate?: Date;
  repeat?: RepeatingPattern;
}

export interface MultiPaymentTreatment {
  payment_terms?: string;
  payment_percentage?: string;
}

export interface FreestyleMethodYear {
  date: string;
  value: string;
  selected: boolean;
}
export interface FreestyleMethodState {
  [key: string]: FreestyleMethodYear[];
}

export interface AvailableLineDataRefNodeWithDepth extends AvailableLineDataRefNode {
  depth: number;
}

export interface PreviewImpactsRequestBody {
  assumption_id?: string;
  assumption_type:
    | ASSUMPTION_TYPES.BASIC
    | ASSUMPTION_TYPES.REPEATING
    | ASSUMPTION_TYPES.UNITS
    | ASSUMPTION_TYPES.FORMULA;
  assumption_data: Partial<AllowedAssumptionTypes>;
  payment_type: PAYMENT_TYPES;
  payment_data: Partial<AllowedPaymentTypes>;
}

export type ConsolidationPreviewImpactsRequestBody = PreviewImpactsRequestBody & {
  organisation_id: string;
};

export type AllowedConsolidationAssumptionTypes =
  | BasicAssumption
  | RepeatingAssumption
  | UnitAssumption
  | FormulaAssumption;

export type AllowedConsolidationPaymentTypes = BasicPaymentType | SinglePaymentType | NonCashPaymentType;

export interface FullConsolidationAssumptionRequest extends FullAssumptionRequest {
  readonly assumption_data: Partial<AllowedConsolidationAssumptionTypes>;
  readonly payment_data: Partial<AllowedConsolidationPaymentTypes>;
}
export interface FullConsolidationAssumptionResponse extends FullAssumptionResponse {
  readonly assumption_data: AllowedConsolidationAssumptionTypes;
  readonly payment_data: AllowedConsolidationPaymentTypes;
}

export const profitAndLossAccountsInOrder: AccountCategory[] = [
  'income',
  'cost_of_sales',
  'expenses',
  'other_income',
  'other_expenses'
];

export const accountCategoriesInOrder: AccountCategory[] = [
  'income',
  'cost_of_sales',
  'expenses',
  'other_income',
  'other_expenses',
  'bank_accounts',
  'fixed_assets',
  'current_assets',
  'non_current_assets',
  'current_liabilities',
  'non_current_liabilities',
  'equity'
];

export interface PredictionHeaderProps {
  children?: ReactNode;
  wrap?: boolean;
}
